import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Modal, Button } from 'nallian-shared-ui/lib/components';
import { Table, Params, Actions } from 'nallian-table-builder-ui';
import { INFINITE_SCROLL, IMPERSONATION_REQUESTS_RESOURCE } from '../constants';
import apiRoutes from '../../../app/api/routes';
import { addParametersToUri } from 'nallian-shared-ui/lib/lib/uri';
import {
  ImpersonationStatus,
  DocumentAccessLabel,
  DurationCell,
  TargetUserCell,
  ActiveUntilCell,
} from 'nallian-shared-ui/lib/impersonation';
import ImpersonationStatusFilter from './table/ImpersonationStatusFilter';
import DocumentAccessFilter from './table/DocumentAccessFilter';
import TargetFilter from './table/TargetFilter';
import ImpersonationRequestDetail from './ImpersonationRequestDetail';

const convertFilters = ({ documentAccessEnabled, ...filters }) => ({
  ...filters,
  documentAccessEnabled:
    documentAccessEnabled === 1
      ? true
      : documentAccessEnabled === 0
      ? false
      : documentAccessEnabled,
});

const FILTER_SETTINGS = {
  convertor: convertFilters,
};

const SORT_SETTINGS = {
  defaults: { createdOn: 'asc' },
};

const ImpersonationRequestsModal = ({ open, onClose, identityId }) => {
  const { t } = useTranslation();
  const [selectedRequestId, setSelectedRequestId] = useState(null);

  const handleViewDetails = useCallback((_, item) => setSelectedRequestId(item.id), []);

  const handleCloseDetail = useCallback(() => setSelectedRequestId(null), []);

  if (!open) return null;

  return (
    <>
      <Modal open closeIcon onClose={onClose} size="large">
        <Modal.Header>{t('impersonationRequests')}</Modal.Header>
        <Modal.Content>
          <Table
            infiniteScroll={INFINITE_SCROLL}
            resource={IMPERSONATION_REQUESTS_RESOURCE}
            url={addParametersToUri(apiRoutes.impersonationRequests(), { identityId })}
            noResultsMessage={t('noImpersonationRequestsFound')}
            pageSize={50}
            rowHeight={52}
            filtered={FILTER_SETTINGS}
            sorted={SORT_SETTINGS}
            noUserSettings
            resizable
          >
            <Actions.Button width="2.5rem" align="center" icon="eye" onClick={handleViewDetails} />
            <Params.Custom
              filter={TargetFilter}
              content={TargetUserCell}
              label="targetUser"
              name="target"
              width="18rem"
              minWidth="16rem"
              grow
              noSort
            />
            <Params.Custom
              content={DurationCell}
              label="duration"
              name="requestedDuration"
              sortName="duration"
              width="8rem"
              noFilter
            />
            <Params.Custom
              filter={ImpersonationStatusFilter}
              content={ImpersonationStatus}
              label="status"
              name="status"
              width="12rem"
            />
            <Params.Custom
              filter={DocumentAccessFilter}
              content={DocumentAccessLabel}
              label="documentAccess"
              name="documentAccessEnabled"
              width="9rem"
            />
            <Params.Custom
              content={ActiveUntilCell}
              name="activeUntil"
              label="activeUntil"
              width="12rem"
              noFilter
              noSort
            />
            <Params.DateTime
              name="createdOn"
              label="requestedAt"
              sortName="requestedAt"
              width="12rem"
              noFilter
            />
          </Table>
        </Modal.Content>
        <Modal.Actions>
          <Button content={t('close')} onClick={onClose} />
        </Modal.Actions>
      </Modal>
      <ImpersonationRequestDetail
        open={!!selectedRequestId}
        requestId={selectedRequestId}
        onClose={handleCloseDetail}
      />
    </>
  );
};

ImpersonationRequestsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  identityId: PropTypes.string,
};

export default ImpersonationRequestsModal;
